import * as LucideIcons from 'lucide-react';
import { LucideIcon } from 'lucide-react';

// Default fallback icon
const DefaultIcon = LucideIcons.LayoutDashboard;

// Cast LucideIcons to a searchable record
const icons = LucideIcons as unknown as Record<string, LucideIcon>;

/**
 * Get icon component by name dynamically from lucide-react
 *
 * Store exact lucide icon names in database, e.g.:
 * - "Leaf" for carbon/environmental
 * - "Users" for social/workforce
 * - "Shield" for governance
 * - "Target" for targets
 * - "FileText" for reports
 *
 * Find all icons at: https://lucide.dev/icons
 */
export const getIcon = (iconName: string | null | undefined): LucideIcon => {
  if (!iconName) return DefaultIcon;

  // Clean the icon name
  const cleanName = iconName.trim();

  // If it looks like a UUID or has "Icon " prefix, return default
  if (/^(Icon\s+)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanName)) {
    return DefaultIcon;
  }

  // Try exact match first (PascalCase - how lucide exports icons)
  if (icons[cleanName]) {
    return icons[cleanName];
  }

  // Try converting to PascalCase (e.g., "layout-dashboard" or "layout_dashboard" -> "LayoutDashboard")
  const pascalCase = cleanName
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

  if (icons[pascalCase]) {
    return icons[pascalCase];
  }

  // Default fallback
  return DefaultIcon;
};

// Category color mapping based on path or category
export const getCategoryColorFromPath = (path: string): string => {
  const normalizedPath = path.toLowerCase();

  if (normalizedPath.includes('environmental') || normalizedPath.includes('carbon') ||
      normalizedPath.includes('energy') || normalizedPath.includes('water') ||
      normalizedPath.includes('waste') || normalizedPath.includes('biodiversity')) {
    return 'text-green-500 dark:text-green-400';
  }

  if (normalizedPath.includes('social') || normalizedPath.includes('workforce') ||
      normalizedPath.includes('dei') || normalizedPath.includes('health') ||
      normalizedPath.includes('training') || normalizedPath.includes('human-rights') ||
      normalizedPath.includes('community')) {
    return 'text-blue-500 dark:text-blue-400';
  }

  if (normalizedPath.includes('governance') || normalizedPath.includes('board') ||
      normalizedPath.includes('compensation') || normalizedPath.includes('ethics') ||
      normalizedPath.includes('risk') || normalizedPath.includes('policies')) {
    return 'text-purple-500 dark:text-purple-400';
  }

  if (normalizedPath.includes('supply') || normalizedPath.includes('supplier')) {
    return 'text-orange-500 dark:text-orange-400';
  }

  if (normalizedPath.includes('analytics') || normalizedPath.includes('materiality') ||
      normalizedPath.includes('target') || normalizedPath.includes('action')) {
    return 'text-indigo-500 dark:text-indigo-400';
  }

  if (normalizedPath.includes('reporting') || normalizedPath.includes('filing') ||
      normalizedPath.includes('generation')) {
    return 'text-pink-500 dark:text-pink-400';
  }

  if (normalizedPath.includes('data-collection') || normalizedPath.includes('carbon-emission')) {
    return 'text-green-500 dark:text-green-400';
  }

  if (normalizedPath.includes('document') || normalizedPath.includes('collection')) {
    return 'text-amber-500 dark:text-amber-400';
  }

  if (normalizedPath.includes('review')) {
    return 'text-teal-500 dark:text-teal-400';
  }

  if (normalizedPath.includes('communication') || normalizedPath.includes('chat')) {
    return 'text-purple-500 dark:text-purple-400';
  }

  if (normalizedPath.includes('admin')) {
    return 'text-indigo-500 dark:text-indigo-400';
  }

  return 'text-slate-500 dark:text-slate-400';
};

export default getIcon;
